import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useChatSocket } from 'src/realtime/ChatSocketProvider';
import { refreshSavedQuoteWorkflow } from './refreshSavedQuoteWorkflow';

/** Only invalidate the displayed event; persisted state remains the authority. */
export function useQuoteWorkflowRefresh(eventId: unknown) {
  const queryClient = useQueryClient();
  const { socket } = useChatSocket();
  useEffect(() => {
    const id = Number(eventId);
    if (!socket || !Number.isSafeInteger(id) || id <= 0) return;
    const refresh = () => { void refreshSavedQuoteWorkflow(queryClient, id); };
    const changed = (message: { eventId?: number }) => {
      if (Number(message?.eventId) === id) refresh();
    };
    socket.on('quote:changed', changed);
    socket.on('connect', refresh);
    return () => {
      socket.off('quote:changed', changed);
      socket.off('connect', refresh);
    };
  }, [socket, eventId, queryClient]);
}

/** Poll only while a provider attempt is in flight. */
export function quoteWorkflowRefreshInterval(quote: any): number | false {
  return ['pending', 'provider_accepted'].includes(quote?.quoteEmailDeliveryStatus) ||
    ['QUEUED', 'SENDING'].includes(quote?.depositEmailDelivery?.status) ? 2000 : false;
}

/** Watch a visible sent Quote tab for later provider bounces. */
export function quotePageRefreshInterval(quote: any, active: boolean): number | false {
  const pendingInterval = quoteWorkflowRefreshInterval(quote);
  if (pendingInterval) return pendingInterval;
  return active && quote?.quoteEmailDeliveryAttemptId &&
    quote?.quoteEmailDeliveryStatus === 'sent' ? 60000 : false;
}
